import { useCallback } from 'react';
import { useUpsertUserFeaturesMutation } from '../queries/upsertUserFeatures.mutation.generated';
import { useDeleteUserFeatureMutation } from '../queries/deleteUserFeature.mutation.generated';
import { GetUserDetailsDocument } from '../queries/getUserDetails.query.generated';
import { useStore } from '@store';

/**
 * Centralized hook for managing user-to-feature links.
 */
export function useFeatureAccess() {
  const [upsertFeatures] = useUpsertUserFeaturesMutation();
  const [removeFeature] = useDeleteUserFeatureMutation();

  /** Grant a user one or more features. */
  const grantFeatures = useCallback(
    async (userId: number, featureIds: number[]) => {
      if (featureIds.length === 0) return;
      const email = useStore.getState().user?.profile?.email || 'portal';
      await upsertFeatures({
        variables: {
          objects: featureIds.map(fid => ({
            user_id: userId,
            feature_id: fid,
            created_by: email,
            updated_by: email,
          })),
        },
        refetchQueries: [{ query: GetUserDetailsDocument, variables: { userId } }],
      });
    },
    [upsertFeatures],
  );

  /** Remove a user's access to one or more features. */
  const revokeFeatures = useCallback(
    async (userId: number, featureIds: number[]) => {
      await Promise.all(
        featureIds.map(fid =>
          removeFeature({ variables: { userId, featureId: fid } }),
        ),
      );
    },
    [removeFeature],
  );

  return { grantFeatures, revokeFeatures };
}
